import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Grid,
  FormControl,
  MenuItem,
  Select,
  OutlinedInput,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import PropTypes from "prop-types";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import useConstants from "constants";
const page_slug = "__all__";

const PageSettings = ({ showAlert, isGlobal = false, api }) => {
  const { t } = useTranslation();
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const { items_per_page_options } = useConstants();
  // State for page settings
  const [settings, setSettings] = useState({
    sortColumn: "id",
    sortDirection: "desc",
    itemsPerPage: 10,
  });

  // Pagination options
  const paginationOptions = items_per_page_options;

  // Sort direction options
  const sortDirectionOptions = [
    { value: "asc", label: t("lang.ascending") },
    { value: "desc", label: t("lang.descending") },
  ];

  // Common sort column options
  const sortColumnOptions = [{ value: "id", label: t("lang.id") }];

  const getPageSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/user-page-settings/`, {
        params: { page_slug: page_slug, is_global: !!isGlobal },
      });
      if (response.status == 200) {
        const data = response.data.data || {};
        if (Object.keys(data).length > 0) {
          setSettings(data);
        }
      }
    } catch (error) {
      showAlert("error", error.response.data.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPageSettings();
  }, []);

  const handleSettingChange = (field, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaveBtnLoading(true);
    try {
      const response = await api.post(`/user-page-settings/`, {
        is_global: isGlobal,
        page_slug: page_slug,
        settings: settings,
      });

      if (response.status == 200) {
        showAlert("success", t("lang.settings_saved_successfully"));
      } else {
        showAlert("error", t("lang.failed_to_save_settings"));
      }
    } catch (error) {
      showAlert("error", error.response.data.error);
    } finally {
      setSaveBtnLoading(false);
    }
    // This would typically make an API call to save the settings
  };

  const handleReset = () => {
    setSettings({
      sortColumn: "id",
      sortDirection: "desc",
      itemsPerPage: 10,
    });
  };

  // Skeleton loader component for form fields
  const FieldSkeleton = () => (
    <SoftBox mb={1}>
      <Skeleton variant="text" sx={{ fontSize: "0.875rem", width: "50%", mb: 1 }} />
      <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
    </SoftBox>
  );

  return (
    <SoftBox>
      <SoftTypography variant="h6" gutterBottom>
        {t("lang.page_settings")}
      </SoftTypography>
      <SoftTypography variant="body2" color="text" mb={3}>
        {t("lang.page_settings_description")}
        {isGlobal
          ? t("lang.this_settings_will_be_applied_to_all_users")
          : t("lang.own_settings_will_be_applied_to_you")}
      </SoftTypography>

      {loading ? (
        // Skeleton Loading State
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FieldSkeleton />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldSkeleton />
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldSkeleton />
          </Grid>
          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
            </SoftBox>
          </Grid>
        </Grid>
      ) : (
        // Actual Form Content
        <Grid container spacing={3}>
          {/* Default Sort Column */}
          <Grid item xs={12} md={4}>
            <SoftBox mb={1}>
              <FormControl fullWidth>
                <SoftTypography variant="overline" gutterBottom>
                  {t("lang.default_sort_column")}
                </SoftTypography>
                <Select
                  value={settings.sortColumn}
                  onChange={(e) => handleSettingChange("sortColumn", e.target.value)}
                  fullWidth
                  input={<OutlinedInput sx={{ height: 56 }} />}
                  sx={{ padding: "10px" }}
                >
                  {sortColumnOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SoftBox>
          </Grid>

          {/* Default Sort Direction */}
          <Grid item xs={12} md={4}>
            <SoftBox mb={1}>
              <FormControl fullWidth>
                <SoftTypography variant="overline" gutterBottom>
                  {t("lang.default_sort_direction")}
                </SoftTypography>
                <Select
                  value={settings.sortDirection}
                  onChange={(e) => handleSettingChange("sortDirection", e.target.value)}
                  fullWidth
                  input={<OutlinedInput sx={{ height: 56 }} />}
                  sx={{ padding: "10px" }}
                >
                  {sortDirectionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SoftBox>
          </Grid>

          {/* Default Pagination Size */}
          <Grid item xs={12} md={4}>
            <SoftBox mb={1}>
              <FormControl fullWidth>
                <SoftTypography variant="overline" gutterBottom>
                  {t("lang.default_pagination_size")}
                </SoftTypography>
                <Select
                  value={settings.itemsPerPage}
                  onChange={(e) => handleSettingChange("itemsPerPage", e.target.value)}
                  fullWidth
                  input={<OutlinedInput sx={{ height: 56 }} />}
                  sx={{ padding: "10px" }}
                >
                  {paginationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option} {t("lang.items_per_page")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SoftBox>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <SoftBox display="flex" justifyContent="flex-end" gap={2} mt={2}>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={handleSave}
                disabled={saveBtnLoading}
                startIcon={saveBtnLoading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {t("lang.save_settings")}
              </SoftButton>
            </SoftBox>
          </Grid>
        </Grid>
      )}
    </SoftBox>
  );
};

PageSettings.propTypes = {
  showAlert: PropTypes.func.isRequired,
  isGlobal: PropTypes.bool,
  api: PropTypes.object.isRequired,
};

export default PageSettings;
